import { init } from '@rematch/core';
import { combineReducers } from 'redux';
import reduxLogic, { createLogicMiddleware } from 'redux-logic';

import * as buildModels from '~/scenes/Build/models';
import * as ComponentTeamModels from '~/scenes/Components/models';
import * as governanceModels from '~/scenes/Governance/models';
import * as identityAndAccessModels from '~/scenes/IdentityAndAccess/models';
import * as monitorModels from '~/scenes/Monitor/models';
import * as othersModels from '~/scenes/Others/models';

const models = {
  ...buildModels,
  ...identityAndAccessModels,
  ...monitorModels,
  ...othersModels,
  ...governanceModels,
  ...ComponentTeamModels
};

const chunk = (arr, size) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
    arr.slice(i * size, i * size + size)
  );

const plugin = (logicMiddlewaresArray, chunkSize) => {
  const arrLogics = [];
  let pluginStore = null;

  return {
    onModel(model) {
      const logics = model.logics || [];
      logics.forEach(logic => {
        const processedLogic = { ...logic };
        if (processedLogic.process) {
          const originalProcess = processedLogic.process;
          processedLogic.process = (context, dispatch, done) => {
            if (!pluginStore) {
              console.error('Store not initialized yet');
              return done();
            }
            return originalProcess(context, pluginStore.dispatch, done);
          };
        }
        arrLogics.push(reduxLogic.createLogic(processedLogic));
      });
    },
    onStoreCreated(store) {
      pluginStore = store;
      const chunks = chunk(arrLogics, chunkSize);
      chunks.forEach((logic, index) => {
        logicMiddlewaresArray[index]?.addLogic?.(logic);
      });
    }
  };
};

const totalLogics = Object.keys(models).reduce((total, key) => {
  const sum = models[key].logics?.length || 0;
  return total + sum;
}, 0);

const CHUNK_SIZE = 40;

const createStore = (navigate = () => {}, initialState = {}) => {
  const createLogicMiddlewareByChunkSize = chunkSize => {
    const chunksSize = Math.ceil(totalLogics / chunkSize);
    const middlewaresArray = [];

    for (let index = 0; index < chunksSize; index += 1) {
      middlewaresArray.push(createLogicMiddleware([], {}));
    }
    return middlewaresArray;
  };

  const middlewaresArray = createLogicMiddlewareByChunkSize(CHUNK_SIZE);

  const store = init({
    models: {
      ...models,
      router: {
        name: 'router',
        state: {},
        reducers: {},
        effects: () => ({
          navigate: args => {
            const types = ['string', 'number'];

            if (types.includes(typeof args)) return navigate(args);
            const { to, options, ...rest } = args;

            if (
              typeof to === 'string' &&
              (to.includes('?') || to.includes('#'))
            ) {
              try {
                const url = new URL(to, 'http://dummy.com');
                const navigateObj = {
                  pathname: url.pathname,
                  ...rest
                };

                if (url.search) navigateObj.search = url.search;
                if (url.hash) navigateObj.hash = url.hash;

                return navigate(navigateObj, options);
              } catch (e) {
                return navigate({ pathname: to, ...rest }, options);
              }
            }

            return navigate({ pathname: to, ...rest }, options);
          }
        })
      }
    },
    plugins: [plugin(middlewaresArray, CHUNK_SIZE)],
    redux: {
      initialState,
      middlewares: middlewaresArray,
      combineReducers: reducers =>
        combineReducers({
          ...reducers
        })
    }
  });

  window.store = store;

  return store;
};

export default createStore;
